import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { TableOptions } from '../../../../../../services/i-bary-table-fetch-service';
import { Pagination } from '../../../../../../models/pagination-table.model';
import { PATHS } from '../../../../../../models/bary-paths.model';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../../../../../services/alert.service';
import { Merchandise } from '../../../../../../models/merchandise.model';
import { MerchandiseService } from '../../../../../../services/merchandise.service';
import { OfferService } from '../../../../../../services/offer.service';
import { StringUtil } from '../../../../util/string-util';
import { mergeMap } from 'rxjs/operators';
import { OfferViewDTO } from '../../../../../../models/offer-view-dto.type';
import { Transporter } from '../../../../../../models/transporter.model';
import { BaryModalService } from '../../../../../../services/bary-modal.service';
import { CurrentDataService } from '../../../../../../services/current-data-service';
import { FieldTypeEnum } from '../../../../../../models/enums/field-type.enum';
import { ColorEnum } from '../../../../../../models/enums/color.enum';

@Component({
  selector: 'jhi-merchandises-list',
  templateUrl: './merchandises-list.component.html',
})
export class MerchandisesListComponent implements OnInit {
  protected readonly baryModalService = inject(BaryModalService);
  protected readonly translateService = inject(TranslateService);
  protected readonly alertService = inject(AlertService);
  protected readonly merchandiseService = inject(MerchandiseService);
  protected readonly offerService = inject(OfferService);
  protected readonly currentDataService = inject(CurrentDataService);

  @Input() merchandises: Merchandise[] = [];
  @Input() tableOptions?: TableOptions;
  @Input() pagination!: Pagination;
  @Output() paginationChange = new EventEmitter<Pagination>();

  public offers: { label: string; offerTarget: OfferViewDTO }[] = [];
  public offersOptions: { value: string; label: string }[] = [];
  public PATHS = PATHS;
  public PRIMARY_COLOR = ColorEnum.PRIMARY;

  public ngOnInit(): void {
    this.currentDataService
      .getCurrentTransporter()
      .pipe(mergeMap((transporter: Transporter | undefined) => this.offerService.getTransporterOffersWithName(transporter!.id!)))
      .subscribe((offers: OfferViewDTO[]): void => {
        this.offersOptions = offers.map(
          (it: OfferViewDTO) =>
            ({ label: it.name, value: it.name } as {
              value: string;
              label: string;
            })
        );
        this.offers = offers.map(
          (it: OfferViewDTO) =>
            ({ label: it.name, offerTarget: it } as {
              label: string;
              offerTarget: OfferViewDTO;
            })
        );
      });
  }

  public transition(): void {
    this.paginationChange.emit(this.pagination);
  }

  public isHidden(): boolean {
    return this.merchandises.length > 0;
  }

  public joinOffers(merchandise: Merchandise): void {
    this.baryModalService
      .openEditWithConfirm({
        label: this.translateService.instant('merchandises.list.labelOffers'),
        messageFn: (offerAlias?: []) =>
          this.translateService.instant('merchandises.list.confirmOffers', {
            oldName: merchandise.oldName,
            withOrWithout: merchandise.forceQuotation ? 'avec' : 'sans',
            offerAlias: StringUtil.nice(offerAlias ?? []),
          }),
        fieldType: FieldTypeEnum.INPUT_MULTI_SELECT,
        value: merchandise.offers?.map(it => it.name),
        fieldOptions: this.offersOptions,
        enableSubmitBtn: true,
      })
      .action()
      .subscribe(action => {
        if (action.submit) {
          merchandise.offers = [];
          this.offers.forEach(it => {
            if (action.value.includes(it.label)) {
              merchandise.offers?.push(it.offerTarget.serialize());
            }
          });
          this.merchandiseService.save(merchandise).subscribe(() => {
            this.transition();
          });
        }
      });
  }

  public rename(merchandise: Merchandise): void {
    this.baryModalService
      .openEditWithConfirm({
        label: this.translateService.instant('merchandises.list.labelRename'),
        messageFn: (newName: string): string =>
          this.translateService.instant('merchandises.list.confirmRename', {
            oldName: merchandise.oldName,
            newName,
            offerAlias: merchandise.offers?.map(it => it.name),
          }),
        fieldType: FieldTypeEnum.INPUT_TEXT,
        value: merchandise.oldName,
      })
      .action()
      .subscribe(action => {
        if (action.submit) {
          merchandise.newName = action.value;
          this.merchandiseService.updateMerchandiseName(merchandise).subscribe(() => {
            this.transition();
          });
        }
      });
  }

  public sortByName(): Merchandise[] {
    return this.merchandises.sort((a, b) => (a!.oldName! > b!.oldName! ? 1 : a.oldName === b.oldName ? 0 : -1));
  }

  public deleteMerchandise(merchandise: Merchandise): void {
    const message = this.translateService.instant('merchandises.list.deleteMerchandise', {
      oldName: merchandise.oldName,
      labelsOffer: merchandise.offers?.map(it => it.name),
    });
    this.baryModalService
      .openConfirm(message)
      .action()
      .subscribe(action => {
        if (action.submit) {
          this.merchandiseService.delete(merchandise).subscribe(() => {
            this.transition();
          });
        }
      });
  }

  public changeActif($event: any, merchandise: Merchandise): void {
    let message;
    merchandise.forceQuotation = $event;
    if ($event) {
      message = this.translateService.instant('merchandises.list.quotationMessage.toTrue', {
        oldName: merchandise.oldName,
        labelsOffer: StringUtil.nice(merchandise.offers?.map(it => it.name) ?? []),
      });
    } else {
      message = this.translateService.instant('merchandises.list.quotationMessage.toFalse', {
        oldName: merchandise.oldName,
        labelsOffer: StringUtil.nice(merchandise.offers?.map(it => it.name) ?? []),
      });
    }

    this.baryModalService
      .openConfirm(message)
      .action()
      .subscribe(action => {
        if (action.submit) {
          this.merchandiseService.updateMerchandiseNameByOffer(merchandise).subscribe(() => {
            this.transition();
          });
        } else {
          merchandise.forceQuotation = !merchandise.forceQuotation;
        }
      });
  }
}
